import { activeApiUrl, SIGN_IN_ATTEMPTED_KEY } from '@/lib/environment';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string | undefined;
  readonly details: unknown;
  /**
   * True when the API marked this response as a POLICY REFUSAL rather than a
   * failure — the backend understood the request and declined it for a reason the
   * operator must read (e.g. the store surface's 409 `edition_locked`, 400
   * `forbidden_fields`). Render these as refusals with the message verbatim: not
   * a warning, not an error toast, and never an invitation to retry.
   */
  readonly refusal: boolean;

  constructor(
    message: string,
    status: number,
    code?: string,
    details?: unknown,
    refusal = false,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
    this.refusal = refusal;
  }

  /** 401 means no valid session — the operator has not signed in, or it expired. */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }

  /** 403 from the API means the operator's role lacks the capability. */
  get isPermissionDenied(): boolean {
    return this.status === 403;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }

  /**
   * True when the request may or may not have been applied — a network drop or
   * gateway timeout. The UI must say so explicitly rather than implying failure,
   * because retrying a Tier 3 override on a request that already landed is its
   * own incident.
   */
  get isIndeterminate(): boolean {
    return this.status === 0 || this.status === 408 || this.status === 504;
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  searchParams?: Record<string, string | number | boolean | undefined>;
  /**
   * Set on every mutation. The API stores the key against the resulting audit
   * record, so a retry after an uncertain network failure is collapsed into the
   * original action instead of applying it twice.
   */
  idempotencyKey?: string;
}

/** Where the Google Workspace SSO flow starts, on the API rather than the console. */
export function signInUrl(): string {
  return `${activeApiUrl}/auth/google/start`;
}

/**
 * Sends an operator with no session to Google, once.
 *
 * The guard matters: authenticating with Google proves identity but grants nothing
 * without a matching `admin_users` row, so a permitted Workspace account that has
 * not been provisioned gets 401 *after* a successful sign-in. Without the flag the
 * browser would bounce between the console and Google indefinitely instead of
 * surfacing the refusal. The flag clears on the first successful response, so a
 * genuinely expired session still redirects on its next request.
 */
function redirectToSignIn(): void {
  if (sessionStorage.getItem(SIGN_IN_ATTEMPTED_KEY) !== null) return;
  sessionStorage.setItem(SIGN_IN_ATTEMPTED_KEY, '1');
  window.location.assign(signInUrl());
}

function buildUrl(path: string, searchParams?: RequestOptions['searchParams']): string {
  const url = new URL(`${activeApiUrl}${path.startsWith('/') ? path : `/${path}`}`);
  if (searchParams) {
    for (const [key, value] of Object.entries(searchParams)) {
      if (value !== undefined) url.searchParams.set(key, String(value));
    }
  }
  return url.toString();
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, signal, searchParams, idempotencyKey } = options;

  const headers: Record<string, string> = { Accept: 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey;

  let response: Response;
  try {
    response = await fetch(buildUrl(path, searchParams), {
      method,
      headers,
      // Session is an httpOnly cookie set by the API after Google SSO. No token
      // ever touches JS, so an XSS cannot lift an admin session.
      credentials: 'include',
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    });
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw new ApiError('Could not reach mesh-admin-api.', 0, 'NETWORK', cause);
  }

  if (response.status === 204) return undefined as T;

  const isJson = response.headers.get('content-type')?.includes('application/json');
  const payload: unknown = isJson ? await response.json() : await response.text();

  if (!response.ok) {
    if (response.status === 401) redirectToSignIn();

    const shape =
      typeof payload === 'object' && payload !== null
        ? (payload as { message?: string; code?: string; details?: unknown; refusal?: boolean })
        : {};
    throw new ApiError(
      shape.message ?? `Request failed with ${String(response.status)}`,
      response.status,
      shape.code,
      shape.details,
      shape.refusal === true,
    );
  }

  sessionStorage.removeItem(SIGN_IN_ATTEMPTED_KEY);
  return payload as T;
}

/** Stable per-attempt key for mutations. */
export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}
